<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDataTable } from './composables/useDataTable'
import { usePivot, useVisualization, useCollaboration, useOfflineSync, useVersionControl } from './composables'
import type { TableColumn, TableData } from './types'
import type { ONEDataTableColumn } from '../../../components/primevuecomponents/data/ONEDataTable/types'
import ONEDataTable from '../../../components/primevuecomponents/data/ONEDataTable/ONEDataTable.vue'
import ONEButton from '../../../components/primevuecomponents/button/ONEButton/ONEButton.vue'
import ONEDropdown from '../../../components/primevuecomponents/form/ONEDropdown/ONEDropdown.vue'

interface Props {
  modelValue: TableData[]
  columns: TableColumn[]
  loading?: boolean
  selectionMode?: 'single' | 'multiple'
  showGridlines?: boolean
  stripedRows?: boolean
  virtualScroll?: boolean
  virtualRowHeight?: number
  collaborationEnabled?: boolean
  versionControlEnabled?: boolean
  offlineMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  columns: () => [],
  loading: false,
  selectionMode: 'multiple',
  showGridlines: true,
  stripedRows: true,
  virtualScroll: false,
  virtualRowHeight: 28,
  collaborationEnabled: false,
  versionControlEnabled: false,
  offlineMode: false
})

const emit = defineEmits<{
  'update:modelValue': [value: TableData[]]
  'selection-change': [selection: TableData[]]
  'column-resize': [event: { element: HTMLElement; delta: number }]
  'filter-change': [filters: Record<string, unknown>]
  'sort': [event: { field: string; order: number }]
  'page': [event: { first: number; rows: number; page: number; pageCount: number }]
  'cell-edit': [event: { data: TableData; field: string; value: unknown }]
}>()

// Initialize i18n
const { t } = useI18n()

// Initialize composables
const table = useDataTable(
  { columns: props.columns, value: props.modelValue },
  (event, ...args) => {
    // @ts-ignore - Type safety handled by component emits
    emit(event, ...args)
  }
)

const pivot = usePivot(
  { columns: props.columns },
  (event, payload) => {
    // @ts-ignore - Type safety handled by component emits
    emit(event, payload)
  }
)

const visualization = useVisualization()

const collaboration = useCollaboration(
  {
    columns: props.columns,
    collaborationConfig: {
      enabled: props.collaborationEnabled,
      websocketUrl: '',
      syncInterval: 1000,
      userTracking: true
    }
  },
  (event, payload) => {
    // @ts-ignore - Type safety handled by component emits
    emit(event, payload)
  }
)

const offlineSync = useOfflineSync(
  {
    columns: props.columns,
    offlineMode: props.offlineMode,
    syncConfig: {
      interval: 5000,
      retryAttempts: 3,
      conflictResolution: 'manual'
    }
  },
  (event, payload) => {
    // @ts-ignore - Type safety handled by component emits
    emit(event, payload)
  }
)

const versionControl = useVersionControl(
  {
    columns: props.columns,
    versionControl: {
      enabled: props.versionControlEnabled,
      maxVersions: 10,
      autoSave: true
    }
  },
  (event, payload) => {
    // @ts-ignore - Type safety handled by component emits
    emit(event, payload)
  }
)

// Map TableColumn dataType to ONEDataTableColumn dataType
const mapDataType = (type: TableColumn['dataType']): ONEDataTableColumn['dataType'] => {
  switch (type) {
    case 'number':
    case 'currency':
      return 'numeric'
    case 'boolean':
      return 'boolean'
    case 'date':
      return 'date'
    case 'chart':
    case 'heatmap':
      return 'custom'
    default:
      return 'text'
  }
}

// Computed properties
const visibleColumns = computed<ONEDataTableColumn[]>(() =>
  table.state.visibleColumns.map(col => ({
    field: col.field,
    header: col.header,
    sortable: col.sortable,
    filterable: col.filterable,
    hidden: col.hidden,
    frozen: col.frozen,
    width: col.width,
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    align: col.align,
    dataType: mapDataType(col.dataType)
  }))
)

const selectedRows = computed({
  get: () => table.state.selectedRows,
  set: (value) => {
    table.state.selectedRows = value
    emit('selection-change', value)
  }
})

const isOffline = computed(() => collaboration.isOffline.value)
const hasUnsyncedChanges = computed(() => offlineSync.pendingChanges.value.length > 0)

// Methods
const onColumnResize = (event: { element: HTMLElement; delta: number }) => {
  emit('column-resize', event)
}

const onFilterChange = (filters: Record<string, unknown>) => {
  emit('filter-change', filters)
}

const onSort = (event: { field: string; order: number }) => {
  emit('sort', event)
}

const onPage = (event: { first: number; rows: number; page: number; pageCount: number }) => {
  emit('page', event)
}

const onCellEdit = (event: { data: TableData; field: string; value: unknown }) => {
  emit('cell-edit', event)
}

const onRefresh = () => {
  table.refreshData()
}

const onExport = () => {
  table.exportData()
}

const onImport = () => {
  table.importData()
}

// Expose methods for parent component
defineExpose({
  refresh: onRefresh,
  export: onExport,
  import: onImport
})
</script>

<template>
  <div class="one-advanced-datatable">
    <!-- Toolbar -->
    <div class="flex justify-between items-center mb-4">
      <div class="flex gap-2">
        <ONEButton
          icon="pi pi-refresh"
          @click="onRefresh"
          :disabled="props.loading"
          :tooltip="t('refresh')"
        />
        <ONEDropdown
          v-model="table.dialogs.exportFormat.value"
          :options="table.dialogs.exportFormats"
          optionLabel="label"
          :placeholder="t('exportAs')"
          :disabled="props.loading"
        />
        <ONEButton
          icon="pi pi-download"
          @click="onExport"
          :disabled="props.loading || !table.dialogs.exportFormat.value"
          :tooltip="t('export')"
        />
        <ONEButton
          icon="pi pi-upload"
          @click="onImport"
          :disabled="props.loading"
          :tooltip="t('import')"
        />
      </div>

      <div class="flex gap-2">
        <ONEButton
          v-if="props.versionControlEnabled"
          icon="pi pi-history"
          @click="table.dialogs.openVersionHistory"
          :disabled="props.loading"
          :tooltip="t('versionHistory')"
        />
        <ONEButton
          v-if="props.collaborationEnabled"
          icon="pi pi-users"
          :badge="collaboration.activeUsers.value.length.toString()"
          :disabled="props.loading"
          :tooltip="t('activeUsers')"
        />
      </div>
    </div>

    <!-- Collaboration Status -->
    <div
      v-if="props.collaborationEnabled && (isOffline || hasUnsyncedChanges)"
      class="p-2 mb-4 bg-yellow-50 text-yellow-700 rounded"
    >
      <template v-if="isOffline">{{ t('workingOffline') }}</template>
      <template v-if="hasUnsyncedChanges">
        ({{ t('unsyncedChanges', { count: offlineSync.pendingChanges.value.length }) }})
      </template>
    </div>

    <!-- Data Table -->
    <ONEDataTable
      v-model:selection="selectedRows"
      :value="props.modelValue"
      :columns="visibleColumns"
      :loading="props.loading"
      :selectionMode="props.selectionMode"
      :showGridlines="props.showGridlines"
      :stripedRows="props.stripedRows"
      :virtualScroll="props.virtualScroll"
      :virtualRowHeight="props.virtualRowHeight"
      :filters="table.state.filters"
      :first="table.state.first"
      :sortField="table.state.sortField"
      :sortOrder="table.state.sortOrder"
      paginator
      :rows="table.state.pageSize"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      dataKey="id"
      responsiveLayout="scroll"
      @column-resize="onColumnResize"
      @filter="onFilterChange"
      @sort="onSort"
      @page="onPage"
      @cell-edit-complete="onCellEdit"
    >
      <template #empty>
        <div class="p-4 text-center text-gray-500">
          {{ t('noRecords') }}
        </div>
      </template>

      <template #loading>
        <div class="p-4 text-center text-gray-500">
          {{ t('loading') }}
        </div>
      </template>
    </ONEDataTable>
  </div>
</template>

<style lang="scss" scoped>
.one-advanced-datatable {
  @apply w-full;

  :deep(.p-datatable) {
    @apply border border-gray-200 rounded;
  }

  :deep(.p-datatable-header) {
    @apply bg-gray-50 border-b border-gray-200;
  }

  :deep(.p-datatable-thead > tr > th) {
    @apply bg-gray-50 text-gray-700 font-semibold;
  }

  :deep(.p-datatable-tbody > tr) {
    @apply hover:bg-gray-50 transition-colors duration-150;

    &.p-highlight {
      @apply bg-blue-50;
    }
  }

  :deep(.p-paginator) {
    @apply bg-gray-50 border-t border-gray-200;
  }
}
</style>
